import { useRef, useState } from "react";
import {
    ChevronLeft,
    Download,
    Upload,
    AlertTriangle,
    Fingerprint,
    Lock as LockIcon,
    KeyRound,
    Plus,
    Trash2,
    RefreshCw,
    Eye,
    EyeOff,
    Copy,
    Check,
} from "lucide-react";
import { C, sBtn, sFull, sTopBar } from "../styles";

export default function Settings({
    authMeta,
    vaultId,
    bioSupported,
    bioCredentials,
    onExport,
    onImport,
    onWipe,
    onBack,
    onAddBio,
    onRemoveBio,
    onSyncNow,
    getRecoveryPhrase,
    showToast,
}) {
    const fileRef = useRef(null);
    const [wiping, setWiping] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [phraseVisible, setPhraseVisible] = useState(false);
    const [phrase, setPhrase] = useState(null);
    const [phraseCopied, setPhraseCopied] = useState(false);
    const [addingBio, setAddingBio] = useState(false);

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const txt = await file.text();
        await onImport(txt.trim());
        e.target.value = "";
    };

    const handleSync = async () => {
        setSyncing(true);
        await onSyncNow();
        setSyncing(false);
    };

    const handleShowPhrase = () => {
        const p = getRecoveryPhrase();
        if (!p) {
            showToast("Phrase available only in sessions unlocked via restore or recovery");
            return;
        }
        setPhrase(p);
        setPhraseVisible(true);
    };

    const copyPhrase = async () => {
        if (!phrase) return;
        try {
            await navigator.clipboard?.writeText(phrase);
            setPhraseCopied(true);
            setTimeout(() => setPhraseCopied(false), 1800);
        } catch {
            showToast("Copy not allowed");
        }
    };

    const handleAddBio = async () => {
        setAddingBio(true);
        await onAddBio();
        setAddingBio(false);
    };

    return (
        <div style={sFull} data-testid="settings-screen">
            <div style={sTopBar}>
                <button onClick={onBack} style={iconBtn} data-testid="settings-back-btn">
                    <ChevronLeft size={16} />
                </button>
                <div
                    style={{
                        fontSize: 12,
                        color: C.muted,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                    }}
                >
                    Settings
                </div>
                <div style={{ width: 40 }} />
            </div>
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "28px 20px 60px",
                    maxWidth: 560,
                    width: "100%",
                    margin: "0 auto",
                }}
            >
                {/* ── Sync ── */}
                <Section title="Sync">
                    <p style={para}>
                        Your vault is mirrored to encrypted cloud storage so you can
                        restore it on any device using your 12-word recovery phrase.
                        Every unlock pulls the latest; every save pushes immediately.
                    </p>
                    <button
                        style={sBtn("secondary")}
                        onClick={handleSync}
                        disabled={syncing}
                        data-testid="settings-sync-btn"
                    >
                        <RefreshCw
                            size={13}
                            style={{
                                verticalAlign: "-2px",
                                marginRight: 8,
                                animation: syncing
                                    ? "hv-spin 0.8s linear infinite"
                                    : "none",
                            }}
                        />
                        {syncing ? "Syncing…" : "Sync now"}
                    </button>
                    <style>{`@keyframes hv-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </Section>

                {/* ── Unlock methods ── */}
                <Section title="Unlock methods">
                    <Row
                        icon={<LockIcon size={14} />}
                        title="6-digit PIN"
                        status={authMeta?.hasPin ? "Enabled" : "Not set"}
                        ok={authMeta?.hasPin}
                    />
                    <Row
                        icon={<KeyRound size={14} />}
                        title="Recovery phrase"
                        status="Configured"
                        ok
                    />
                    {/* Bio devices */}
                    <div
                        style={{
                            marginTop: 8,
                            border: `1px solid ${C.border}`,
                            borderRadius: 10,
                            background: C.surface,
                            overflow: "hidden",
                        }}
                        data-testid="settings-bio-section"
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px 14px",
                                borderBottom:
                                    bioCredentials.length > 0
                                        ? `1px solid ${C.border}`
                                        : "none",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    color: C.text,
                                    fontSize: 13,
                                }}
                            >
                                <Fingerprint size={14} color={C.accent} />
                                Biometric devices
                            </div>
                            <div
                                style={{
                                    fontSize: 11,
                                    color: C.dim,
                                    letterSpacing: "0.1em",
                                }}
                            >
                                {bioCredentials.length} ENROLLED
                            </div>
                        </div>
                        {bioCredentials.map((c) => (
                            <div
                                key={c.credential_id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "10px 14px",
                                    borderBottom: `1px solid ${C.border}`,
                                    fontSize: 12,
                                }}
                                data-testid="settings-bio-row"
                            >
                                <div style={{ color: C.text }}>
                                    {c.label || "Unnamed device"}
                                    <span
                                        style={{
                                            color: C.dim,
                                            marginLeft: 8,
                                            fontSize: 10,
                                            letterSpacing: "0.05em",
                                        }}
                                    >
                                        {c.credential_id.slice(0, 10)}…
                                    </span>
                                </div>
                                <button
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                `Remove "${c.label || "this device"}" from the vault? It will no longer unlock via biometric.`,
                                            )
                                        )
                                            onRemoveBio(c.credential_id);
                                    }}
                                    style={{
                                        background: "transparent",
                                        border: `1px solid ${C.border}`,
                                        borderRadius: 8,
                                        color: C.danger,
                                        padding: "5px 8px",
                                        cursor: "pointer",
                                    }}
                                    data-testid="settings-bio-remove-btn"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                        {bioSupported && (
                            <button
                                onClick={handleAddBio}
                                disabled={addingBio}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    width: "100%",
                                    padding: "11px 14px",
                                    background: "transparent",
                                    border: "none",
                                    borderTop:
                                        bioCredentials.length > 0
                                            ? `1px solid ${C.border}`
                                            : "none",
                                    color: C.accent,
                                    fontSize: 12,
                                    cursor: "pointer",
                                    fontFamily: C.font,
                                }}
                                data-testid="settings-add-bio-btn"
                            >
                                <Plus size={13} />
                                {addingBio
                                    ? "Enrolling…"
                                    : bioCredentials.length > 0
                                      ? "Add another device"
                                      : "Enable Face ID / Touch ID on this device"}
                            </button>
                        )}
                        {!bioSupported && bioCredentials.length === 0 && (
                            <div
                                style={{
                                    padding: "12px 14px",
                                    color: C.dim,
                                    fontSize: 11,
                                }}
                            >
                                This device has no biometric hardware available.
                            </div>
                        )}
                    </div>
                </Section>

                {/* ── Add another device ── */}
                <Section title="Add another device">
                    <p style={para}>
                        To open the same vault on a phone or another computer,
                        install Hikari Vault on it and choose{" "}
                        <b style={{ color: C.text }}>Restore existing vault</b> —
                        then enter your 12-word recovery phrase.
                    </p>
                    {!phraseVisible ? (
                        <button
                            style={sBtn("secondary")}
                            onClick={handleShowPhrase}
                            data-testid="settings-show-phrase-btn"
                        >
                            <Eye
                                size={13}
                                style={{ verticalAlign: "-2px", marginRight: 8 }}
                            />
                            Show my recovery phrase
                        </button>
                    ) : (
                        <>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(3, 1fr)",
                                    gap: 8,
                                    background: "rgba(11,126,163,0.06)",
                                    border: `1px dashed ${C.accent}55`,
                                    borderRadius: 12,
                                    padding: 14,
                                    marginBottom: 10,
                                }}
                                data-testid="settings-phrase-grid"
                            >
                                {phrase.split(" ").map((w, i) => (
                                    <div
                                        key={i}
                                        style={{ fontSize: 12, color: C.text }}
                                    >
                                        <span style={{ color: C.dim, marginRight: 6 }}>
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        {w}
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    style={{ ...sBtn("secondary"), marginBottom: 0 }}
                                    onClick={copyPhrase}
                                    data-testid="settings-copy-phrase-btn"
                                >
                                    {phraseCopied ? (
                                        <>
                                            <Check
                                                size={13}
                                                style={{ verticalAlign: "-2px", marginRight: 6 }}
                                            />
                                            Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy
                                                size={13}
                                                style={{ verticalAlign: "-2px", marginRight: 6 }}
                                            />
                                            Copy
                                        </>
                                    )}
                                </button>
                                <button
                                    style={{ ...sBtn("ghost"), marginBottom: 0 }}
                                    onClick={() => {
                                        setPhraseVisible(false);
                                        setPhrase(null);
                                    }}
                                    data-testid="settings-hide-phrase-btn"
                                >
                                    <EyeOff
                                        size={13}
                                        style={{ verticalAlign: "-2px", marginRight: 6 }}
                                    />
                                    Hide
                                </button>
                            </div>
                        </>
                    )}
                </Section>

                {/* ── Backup ── */}
                <Section title="Backup">
                    <p style={para}>
                        Export creates an AES-256-GCM encrypted{" "}
                        <code style={codeStyle}>.hvault</code> file. It can
                        only be decrypted from within an unlocked vault
                        with the same master key.
                    </p>
                    <button
                        style={sBtn("secondary")}
                        onClick={onExport}
                        data-testid="settings-export-btn"
                    >
                        <Download
                            size={13}
                            style={{ verticalAlign: "-2px", marginRight: 8 }}
                        />
                        Export encrypted backup (.hvault)
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".hvault,text/plain"
                        onChange={handleImport}
                        style={{ display: "none" }}
                        data-testid="settings-import-file-input"
                    />
                    <button
                        style={sBtn("secondary")}
                        onClick={() => fileRef.current?.click()}
                        data-testid="settings-import-btn"
                    >
                        <Upload
                            size={13}
                            style={{ verticalAlign: "-2px", marginRight: 8 }}
                        />
                        Import .hvault backup
                    </button>
                </Section>

                {/* ── Vault info ── */}
                <Section title="Vault">
                    <div style={para}>
                        Vault ID (derived from your recovery phrase)
                        <div
                            style={{
                                color: C.dim,
                                fontSize: 11,
                                wordBreak: "break-all",
                                marginTop: 4,
                            }}
                            data-testid="settings-vault-id"
                        >
                            {vaultId || "—"}
                        </div>
                    </div>
                </Section>

                {/* ── Danger zone ── */}
                <Section title="Danger zone">
                    <p style={para}>
                        Wiping removes every trace of the vault — from this
                        device AND our encrypted cloud storage. This
                        affects{" "}
                        <b style={{ color: C.danger }}>all your devices</b>{" "}
                        and is{" "}
                        <b style={{ color: C.danger }}>irreversible</b>{" "}
                        unless you have an export.
                    </p>
                    <button
                        style={sBtn("danger")}
                        onClick={async () => {
                            if (
                                !window.confirm(
                                    "This permanently wipes your vault on ALL devices. Continue?",
                                )
                            )
                                return;
                            setWiping(true);
                            await onWipe();
                            setWiping(false);
                        }}
                        data-testid="settings-wipe-btn"
                    >
                        <AlertTriangle
                            size={13}
                            style={{ verticalAlign: "-2px", marginRight: 8 }}
                        />
                        {wiping ? "Wiping…" : "Wipe vault everywhere"}
                    </button>
                </Section>

                <div
                    style={{
                        textAlign: "center",
                        fontSize: 10,
                        letterSpacing: "0.2em",
                        color: C.dim,
                        marginTop: 32,
                    }}
                >
                    HIKARI LABS · ZERO-KNOWLEDGE · v2
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div
                style={{
                    fontSize: 10,
                    letterSpacing: "0.3em",
                    color: C.accent,
                    textTransform: "uppercase",
                    marginBottom: 14,
                }}
            >
                {title}
            </div>
            {children}
        </div>
    );
}

function Row({ icon, title, status, ok }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                marginBottom: 8,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: C.text,
                    fontSize: 13,
                }}
            >
                <span style={{ color: C.accent }}>{icon}</span>
                {title}
            </div>
            <div
                style={{
                    fontSize: 11,
                    color: ok ? C.ok : C.dim,
                    letterSpacing: "0.1em",
                }}
            >
                {status.toUpperCase()}
            </div>
        </div>
    );
}

const iconBtn = {
    background: "transparent",
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    color: C.muted,
    padding: "7px 12px",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    fontFamily: C.font,
    fontSize: 12,
};

const para = {
    fontSize: 12,
    color: C.muted,
    lineHeight: 1.6,
    marginBottom: 14,
};

const codeStyle = {
    background: "rgba(11,126,163,0.1)",
    color: C.accent,
    padding: "2px 6px",
    borderRadius: 4,
    fontSize: 11,
};
